/**
 * AccountSecurity Page
 *
 * Settings page for managing authentication and security settings.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 3 "Account Security Page"
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  multiFactor,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useMFAEnrollmentCheck } from '../../hooks/useMFAEnrollmentCheck';
import { SettingsLayout } from '../../components/settings/SettingsLayout';
import { PasswordStrengthIndicator } from '../../components/PasswordStrengthIndicator';
import { validatePassword } from '../../utils/passwordValidation';
import {
  Lock,
  Smartphone,
  Check,
  AlertCircle,
  Loader2,
  X,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
}

function ChangePasswordModal({
  isOpen,
  onClose,
  userEmail,
}: ChangePasswordModalProps): JSX.Element | null {
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !userEmail) return;

    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError('Please meet all password requirements');
      return;
    }

    setIsChanging(true);

    try {
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(userEmail, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err: any) {
      console.error('Error updating password:', err);
      if (err?.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err?.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setIsChanging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-ink">Change Password</h3>
            <button onClick={onClose} className="btn-ghost p-2 -mr-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-evergreen" />
              </div>
              <p className="text-lg font-semibold text-ink">Password Updated</p>
              <p className="text-muted text-sm mt-1">Your password has been changed successfully.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="alert alert-error flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="acct-current-password" className="label">Current Password</label>
                <input
                  id="acct-current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="input"
                  required
                  disabled={isChanging}
                />
              </div>

              <div>
                <label htmlFor="acct-new-password" className="label">New Password</label>
                <input
                  id="acct-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="input"
                  required
                  disabled={isChanging}
                />
                {newPassword && (
                  <PasswordStrengthIndicator password={newPassword} className="mt-3" />
                )}
              </div>

              <div>
                <label htmlFor="acct-confirm-password" className="label">Confirm New Password</label>
                <input
                  id="acct-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="input"
                  required
                  disabled={isChanging}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary flex-1"
                  disabled={isChanging}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={isChanging}
                >
                  {isChanging ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

// ─── MFA Enrollment Modal (for settings page) ───────────────────────────────

type MFAModalStep = 'phone' | 'verify' | 'success';

interface MFAEnrollModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

function MFAEnrollModal({ isOpen, onClose, onEnrolled }: MFAEnrollModalProps): JSX.Element | null {
  const { user } = useAuth();

  const [step, setStep] = useState<MFAModalStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  const initRecaptcha = useCallback(() => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    if (recaptchaContainerRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
        size: 'invisible',
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      initRecaptcha();
    }
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, [isOpen, initRecaptcha]);

  const formatPhoneDisplay = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 10) setPhoneNumber(digits);
  };

  const getErrorMessage = (err: any): string => {
    const code = err?.code || '';
    switch (code) {
      case 'auth/invalid-phone-number':
        return 'Please enter a valid US phone number.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes.';
      case 'auth/invalid-verification-code':
        return "That code didn't match. Please try again.";
      case 'auth/code-expired':
        return 'The verification code has expired. Please request a new one.';
      case 'auth/requires-recent-login':
        return 'Your session has expired. Please sign out and sign back in.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || phoneNumber.length !== 10) return;

    setError(null);
    setIsLoading(true);

    try {
      if (!recaptchaVerifierRef.current) initRecaptcha();

      const multiFactorSession = await multiFactor(user).getSession();
      const phoneInfoOptions = {
        phoneNumber: `+1${phoneNumber}`,
        session: multiFactorSession,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierRef.current!
      );

      setVerificationId(verId);
      setStep('verify');
    } catch (err: any) {
      console.error('Error sending MFA code:', err);
      setError(getErrorMessage(err));
      initRecaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !verificationId || verificationCode.length !== 6) return;

    setError(null);
    setIsLoading(true);

    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(user).enroll(multiFactorAssertion, 'Phone');
      setStep('success');
      onEnrolled();
    } catch (err: any) {
      console.error('Error verifying MFA code:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('phone');
    setPhoneNumber('');
    setVerificationCode('');
    setVerificationId('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-40" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-ink">
              {step === 'success' ? 'MFA Enabled' : 'Set Up SMS Verification'}
            </h3>
            <button onClick={handleClose} className="btn-ghost p-2 -mr-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          {step === 'phone' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="label">Phone Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 bg-airy border border-soft-neutral rounded-lg text-sm text-muted">+1</div>
                  <input
                    type="tel"
                    value={formatPhoneDisplay(phoneNumber)}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className="input flex-1"
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                <p className="helper-text mt-1.5">We'll send a code to this number when you sign in.</p>
              </div>

              {error && (
                <div className="alert alert-error flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleClose} className="btn-secondary flex-1" disabled={isLoading}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={isLoading || phoneNumber.length !== 10}
                >
                  {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send Code'}
                </button>
              </div>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <p className="text-sm text-muted text-center">
                Enter the 6-digit code sent to <span className="font-semibold text-ink">+1 {formatPhoneDisplay(phoneNumber)}</span>
              </p>
              <div>
                <label className="label">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={verificationCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 6) setVerificationCode(val);
                  }}
                  placeholder="000000"
                  className="input text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              {error && (
                <div className="alert alert-error flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setStep('phone'); setVerificationCode(''); setError(null); }} className="btn-secondary flex-1" disabled={isLoading}>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={isLoading || verificationCode.length !== 6}
                >
                  {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : 'Verify'}
                </button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-evergreen" />
              </div>
              <p className="text-lg font-semibold text-ink">SMS Verification Enabled</p>
              <p className="text-muted text-sm mt-1">Your account is now protected with two-factor authentication.</p>
              <button onClick={handleClose} className="btn-primary mt-6">Done</button>
            </div>
          )}
        </div>
      </div>
      <div ref={recaptchaContainerRef} id="recaptcha-container-settings" />
    </>
  );
}

// ─── Unenroll Confirmation Modal ─────────────────────────────────────────────

interface UnenrollConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: string | null;
}

function UnenrollConfirmModal({ isOpen, onClose, onConfirm, isLoading, error }: UnenrollConfirmModalProps): JSX.Element | null {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ink">Remove Two-Factor Authentication?</h3>
            <button onClick={onClose} className="btn-ghost p-2 -mr-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-muted mb-4">
            Removing 2FA will make your account less secure. As a HIPAA-compliant platform, Hesvara strongly recommends keeping 2FA enabled. You will be required to re-enroll on your next sign-in.
          </p>

          {error && (
            <div className="alert alert-error flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1" disabled={isLoading}>
              Keep 2FA
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Removing...</> : 'Remove 2FA'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── AccountSecurity Page ────────────────────────────────────────────────────

export function AccountSecurity(): JSX.Element {
  const { user } = useAuth();
  const { isEnrolled, loading: mfaLoading } = useMFAEnrollmentCheck();

  // UI state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showMFAEnrollModal, setShowMFAEnrollModal] = useState(false);
  const [showUnenrollModal, setShowUnenrollModal] = useState(false);
  const [isUnenrolling, setIsUnenrolling] = useState(false);
  const [unenrollError, setUnenrollError] = useState<string | null>(null);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);

  // Sync local state with hook
  useEffect(() => {
    setMfaEnrolled(isEnrolled);
  }, [isEnrolled]);

  // Get the masked phone number from enrolled factors
  const getMaskedPhone = (): string => {
    if (!user) return '';
    try {
      const factors = multiFactor(user).enrolledFactors;
      const phoneFactor = factors.find(
        (f) => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      );
      if (phoneFactor && 'phoneNumber' in phoneFactor) {
        const phone = (phoneFactor as any).phoneNumber as string;
        // Show last 4 digits masked
        if (phone.length >= 4) {
          return `\u2022\u2022\u2022\u2022\u2022\u2022${phone.slice(-4)}`;
        }
        return phone;
      }
    } catch {
      // ignore
    }
    return '';
  };

  const handleUnenroll = async () => {
    if (!user) return;
    setUnenrollError(null);
    setIsUnenrolling(true);

    try {
      const factors = multiFactor(user).enrolledFactors;
      const phoneFactor = factors.find(
        (f) => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      );

      if (phoneFactor) {
        await multiFactor(user).unenroll(phoneFactor);
        setMfaEnrolled(false);
        setShowUnenrollModal(false);
      }
    } catch (err: any) {
      console.error('Error unenrolling MFA:', err);
      if (err?.code === 'auth/requires-recent-login') {
        setUnenrollError('Your session has expired. Please sign out and sign back in to make this change.');
      } else {
        setUnenrollError('Failed to remove 2FA. Please try again.');
      }
    } finally {
      setIsUnenrolling(false);
    }
  };

  // Calculate "last changed" text (placeholder - Firebase doesn't track this)
  const getPasswordLastChanged = (): string => {
    return 'Password last changed: Unknown';
  };

  return (
    <SettingsLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-[28px] font-bold text-ink">Account Security</h2>
        <p className="text-muted text-[15px]">Manage your account security settings</p>
      </div>

      {/* Email & Password Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Email & Password</h3>

          {/* Current Email */}
          <div className="mb-6">
            <label className="label">Current Email</label>
            <p className="text-ink py-3">{user?.email}</p>
          </div>

          {/* Password */}
          <div className="flex items-center justify-between pt-4 border-t border-soft-neutral">
            <div>
              <label className="label mb-0">Password</label>
              <p className="text-sm text-muted">{getPasswordLastChanged()}</p>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-ink">Two-Factor Authentication</h3>
            {!mfaLoading && (
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  mfaEnrolled
                    ? 'bg-evergreen/10 text-evergreen'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {mfaEnrolled ? (
                  <><ShieldCheck className="w-3.5 h-3.5" /> Enabled</>
                ) : (
                  <><ShieldOff className="w-3.5 h-3.5" /> Not Enabled</>
                )}
              </span>
            )}
          </div>
          <p className="text-sm text-muted mb-5">
            SMS verification is required for HIPAA compliance
          </p>

          <div className="flex items-center justify-between p-4 bg-airy rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                mfaEnrolled ? 'bg-evergreen/10' : 'bg-soft-neutral'
              }`}>
                <Smartphone className={`w-5 h-5 ${mfaEnrolled ? 'text-evergreen' : 'text-muted'}`} />
              </div>
              <div>
                <p className="font-semibold text-ink">SMS Verification</p>
                <p className="text-sm text-muted">
                  {mfaLoading
                    ? 'Checking...'
                    : mfaEnrolled
                    ? `Phone: ${getMaskedPhone()}`
                    : 'Not configured'}
                </p>
              </div>
            </div>
            {mfaEnrolled ? (
              <button
                onClick={() => setShowUnenrollModal(true)}
                className="btn-secondary text-sm"
              >
                Remove
              </button>
            ) : (
              <button
                onClick={() => setShowMFAEnrollModal(true)}
                className="btn-primary text-sm flex items-center gap-2"
              >
                <Smartphone className="w-4 h-4" />
                Set Up
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userEmail={user?.email || ''}
      />

      {/* MFA Enrollment Modal */}
      <MFAEnrollModal
        isOpen={showMFAEnrollModal}
        onClose={() => setShowMFAEnrollModal(false)}
        onEnrolled={() => setMfaEnrolled(true)}
      />

      {/* Unenroll Confirmation Modal */}
      <UnenrollConfirmModal
        isOpen={showUnenrollModal}
        onClose={() => { setShowUnenrollModal(false); setUnenrollError(null); }}
        onConfirm={handleUnenroll}
        isLoading={isUnenrolling}
        error={unenrollError}
      />
    </SettingsLayout>
  );
}

export default AccountSecurity;
